import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import { Checkbox } from '@/components/base/checkbox/checkbox'
import { LoadingSpinner } from '@/components/common'
import { cn } from '@/lib/utils'

export interface InboxFilterOption<T extends string | number> {
  value: T
  label: string
}

interface InboxFilterOptionsProps<T extends string | number> {
  options: InboxFilterOption<T>[]
  selected: T[]
  onChange: (selected: T[]) => void
  /** One choice replaces the previous one. Defaults to several. */
  selection?: 'single' | 'multiple'
  /** Lets the search text be selected as a value of its own; only for string values */
  allowsCustomValue?: boolean
  /** Controlled search text. The assistant filter owns this so it can query the server. */
  query?: string
  onQueryChange?: (query: string) => void
  /** When false, `options` are already the search result. */
  filterLocally?: boolean
  isLoading?: boolean
  /**
   * Focus the search field on mount. Desktop menus want this.
   * The mobile sheet must not, or the keyboard covers the list.
   */
  autoFocus?: boolean
  /**
   * Scroll the choices inside this list. Desktop menus need that.
   * The mobile sheet scrolls itself, so a second scroller makes the drag stick.
   */
  scrollable?: boolean
  /** Show this many choices until the rest are expanded. Omit to show the whole list. */
  previewLimit?: number
}

export function InboxFilterOptions<T extends string | number>({
  options,
  selected,
  onChange,
  selection = 'multiple',
  allowsCustomValue = false,
  query: controlledQuery,
  onQueryChange,
  filterLocally = true,
  isLoading = false,
  autoFocus = true,
  scrollable = true,
  previewLimit,
}: InboxFilterOptionsProps<T>) {
  const { t } = useTranslation()
  // A phone opens the keyboard for a focused text field even without autoFocus.
  // Stay read-only until the user taps, and use 16px type so iOS does not zoom.
  const [editable, setEditable] = useState(autoFocus)
  const [expanded, setExpanded] = useState(false)
  const [uncontrolledQuery, setUncontrolledQuery] = useState('')
  const query = controlledQuery ?? uncontrolledQuery
  const setQuery = (next: string) => {
    if (controlledQuery === undefined) setUncontrolledQuery(next)
    onQueryChange?.(next)
  }

  const allOptions = useMemo(() => {
    if (!allowsCustomValue) return options
    const custom = selected
      .filter((value) => !options.some((option) => option.value === value))
      .map((value) => ({ value, label: String(value) }))
    return [...options, ...custom]
  }, [allowsCustomValue, options, selected])

  const visibleOptions = useMemo(() => {
    if (!filterLocally) return allOptions
    const q = query.trim().toLowerCase()
    return q ? allOptions.filter((option) => option.label.toLowerCase().includes(q)) : allOptions
  }, [allOptions, filterLocally, query])

  const shownOptions = useMemo(() => {
    if (previewLimit == null || expanded || query.trim() !== '') return visibleOptions
    const picked = visibleOptions.filter((option) => selected.includes(option.value))
    const rest = visibleOptions.filter((option) => !selected.includes(option.value))
    return [...picked, ...rest.slice(0, Math.max(previewLimit - picked.length, 0))]
  }, [expanded, previewLimit, query, selected, visibleOptions])
  const hiddenCount = visibleOptions.length - shownOptions.length
  const canTogglePreview =
    previewLimit != null && query.trim() === '' && visibleOptions.length > previewLimit

  const customValue = query.trim() as T
  const showCustomValue =
    allowsCustomValue &&
    query.trim() !== '' &&
    !allOptions.some((option) => option.value === customValue || option.label === customValue)

  const toggle = (value: T, isSelected: boolean) => {
    if (selection === 'single') {
      onChange(isSelected ? [value] : [])
      return
    }
    onChange(isSelected ? [...selected, value] : selected.filter((item) => item !== value))
  }

  return (
    <>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <Search
          className="size-4 shrink-0 text-gray-700 dark:text-gray-300"
          strokeWidth={2.5}
          aria-hidden="true"
        />
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('resources.conversations.inbox.filter_search')}
          aria-label={t('resources.conversations.inbox.filter_search')}
          className={cn(
            'min-w-0 flex-1 bg-transparent text-gray-900 outline-hidden! placeholder:text-gray-400 dark:text-gray-100',
            autoFocus ? 'text-sm' : 'text-base'
          )}
          autoFocus={autoFocus}
          readOnly={!editable}
          inputMode={editable ? 'text' : 'none'}
          onPointerDown={(event) => {
            event.currentTarget.readOnly = false
            event.currentTarget.inputMode = 'text'
            setEditable(true)
          }}
        />
      </div>
      <div className="h-px bg-gray-200 dark:bg-gray-700" />

      {showCustomValue && (
        <button
          type="button"
          className="w-full cursor-pointer truncate px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
          onClick={() => {
            onChange(selection === 'single' ? [customValue] : [...selected, customValue])
            setQuery('')
          }}
        >
          {t('resources.conversations.inbox.filter_custom', { value: query.trim() })}
        </button>
      )}

      {isLoading && visibleOptions.length === 0 ? (
        <div className="flex justify-center py-6">
          <LoadingSpinner size="sm" />
        </div>
      ) : visibleOptions.length > 0 ? (
        <>
          <div
            className={`flex items-center px-3 py-2 text-sm leading-[18px] ${selection === 'multiple' ? 'justify-between' : 'justify-end'}`}
          >
            {selection === 'multiple' && (
              <button
                type="button"
                className="cursor-pointer text-gray-700 hover:underline dark:text-gray-200"
                onClick={() => onChange(allOptions.map((option) => option.value))}
              >
                {t('resources.conversations.inbox.select_all')}
              </button>
            )}
            <button
              type="button"
              className="cursor-pointer text-gray-500 hover:underline dark:text-gray-400"
              onClick={() => onChange([])}
            >
              {t('resources.generic.clear')}
            </button>
          </div>
          <div className={scrollable ? 'max-h-72 overflow-y-auto py-1' : 'py-1'}>
            {shownOptions.map((option) => (
              <Checkbox
                key={option.value}
                label={<span className="leading-[18px] font-normal">{option.label}</span>}
                isSelected={selected.includes(option.value)}
                onChange={(isSelected) => toggle(option.value, isSelected)}
                className="w-full cursor-pointer items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800"
              />
            ))}
            {canTogglePreview && (
              <button
                type="button"
                className="w-full cursor-pointer px-3 py-2 text-left text-sm text-gray-500 hover:underline"
                onClick={() => setExpanded((open) => !open)}
              >
                {expanded
                  ? t('resources.conversations.inbox.show_less_options')
                  : t('resources.conversations.inbox.show_more_options', { count: hiddenCount })}
              </button>
            )}
          </div>
        </>
      ) : (
        !showCustomValue && (
          <div className="flex flex-col items-center gap-1 px-4 py-6 text-center">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
              {t('resources.conversations.inbox.no_results')}
            </span>
            <span className="text-xs text-gray-400">
              {t('resources.conversations.inbox.no_results_hint')}
            </span>
          </div>
        )
      )}
    </>
  )
}
